import {
  Button,
  Dialog,
  EmptyNote,
  Popover,
} from "@scribenote/brian-design-system";
import { useEffect, useLayoutEffect, useRef, useState } from "react";

import { candidateEvidence, candidateVerdict } from "./candidateEvidence";
import { PolicyConditionPreview, PolicyConditions } from "./PolicyConditions";
import { duration, readableEvidence, words } from "./traceFormat";
import type { TraceAction, TraceDetail } from "./types";

const Evidence = ({ label, value }: { label: string; value: unknown }) => (
  <div className="min-w-0">
    <p className="text-annotation-caps text-annotation">{label}</p>
    <pre className="mt-1 max-h-48 overflow-auto whitespace-pre-wrap break-all rounded-paper border border-rule bg-sheet p-2 font-mono text-xs text-ink-body">
      {readableEvidence(value)}
    </pre>
  </div>
);

const ActionResult = ({
  action,
  highlighted,
  ref,
}: {
  action: TraceAction;
  highlighted: boolean;
  ref?: (node: HTMLLIElement | null) => void;
}) => (
  <li
    ref={ref}
    className={`relative rounded-paper border bg-card p-3 ${highlighted ? "border-accent" : "border-rule"}`}
  >
    <p className="font-medium text-ink">{words(action.action_type)}</p>
    <p className="mt-1 font-mono text-xs text-annotation">
      Execution: {action.run_state ? words(action.run_state) : "Pending"}
      {action.run_outcome ? ` · outcome: ${words(action.run_outcome)}` : ""}
      {action.run_attempts !== null
        ? ` · ${action.run_attempts} attempt${action.run_attempts === 1 ? "" : "s"}`
        : ""}
    </p>
    {action.last_error && (
      <p className="mt-2 text-xs text-ink-body">
        Recorded error: {action.last_error}
      </p>
    )}
    {(action.arguments !== null || action.preconditions !== null) && (
      <details className="mt-2 text-xs text-annotation">
        <summary className="cursor-pointer">
          Recorded execution evidence
        </summary>
        <div className="mt-2 space-y-2">
          <Evidence label="Arguments" value={action.arguments} />
          <Evidence label="Preconditions" value={action.preconditions} />
        </div>
      </details>
    )}
  </li>
);

interface Branch {
  candidateUuid: string;
  target: string;
  path: string;
}

interface BranchLayout {
  width: number;
  height: number;
  branches: Branch[];
}

export const PolicyTrace = ({ trace }: { trace: TraceDetail }) => {
  const [stepName, setStepName] = useState<string | null>(null);
  const [drawerCandidateUuid, setDrawerCandidateUuid] = useState<string | null>(
    null,
  );
  const [previewCandidate, setPreviewCandidate] = useState<string | null>(null);
  const previewExitTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const steps = trace.steps;
  const step = steps.find((item) => item.name === stepName) ?? steps[0];
  const drawerCandidate =
    step?.candidates.find((item) => item.uuid === drawerCandidateUuid) ?? null;
  const cancelPreviewExit = () => {
    if (previewExitTimer.current !== null) {
      clearTimeout(previewExitTimer.current);
      previewExitTimer.current = null;
    }
  };
  const schedulePreviewExit = () => {
    cancelPreviewExit();
    previewExitTimer.current = setTimeout(() => {
      setPreviewCandidate(null);
      previewExitTimer.current = null;
    }, 150);
  };
  useEffect(
    () => () => {
      if (previewExitTimer.current !== null)
        clearTimeout(previewExitTimer.current);
    },
    [],
  );
  const loadedContext = step
    ? (step.loaded ??
      (step.name === "root" && step.parent_candidate_index === null
        ? trace.decision?.loaded
        : null))
    : trace.decision?.loaded;
  const savedTurn = loadedContext?.turn;
  const savedTurnUuid =
    savedTurn && typeof savedTurn === "object" && "uuid" in savedTurn
      ? savedTurn.uuid
      : null;
  const matchingCurrentSource =
    trace.summary.source?.current &&
    typeof savedTurnUuid === "string" &&
    trace.summary.source.uuid === savedTurnUuid
      ? trace.summary.source
      : null;
  const drawerEvidence = drawerCandidate
    ? candidateEvidence(drawerCandidate)
    : null;
  const earlierSelected =
    drawerCandidate && step
      ? step.candidates.find(
          (item) =>
            item.chosen &&
            item.candidate_index < drawerCandidate.candidate_index,
        )
      : null;
  const { actions, childSteps, branchPairs } = (() => {
    const selected = step?.candidates.filter((item) => item.chosen) ?? [];
    const selectedIds = new Set(selected.map((item) => item.uuid));
    const selectedActions = trace.actions.filter(
      (item) => item.candidate_uuid && selectedIds.has(item.candidate_uuid),
    );
    const children = steps.filter(
      (item) =>
        item.name !== step?.name &&
        selected.some(
          (candidate) =>
            item.parent_candidate_index === candidate.candidate_index,
        ),
    );
    const pairs = selected.flatMap((candidate) => [
      ...selectedActions
        .filter((action) => action.candidate_uuid === candidate.uuid)
        .map((action) => ({
          candidateUuid: candidate.uuid,
          target: `action:${action.uuid}`,
        })),
      ...children
        .filter(
          (child) => child.parent_candidate_index === candidate.candidate_index,
        )
        .map((child) => ({
          candidateUuid: candidate.uuid,
          target: `step:${child.name}`,
        })),
    ]);
    return {
      actions: selectedActions,
      childSteps: children,
      branchPairs: pairs,
    };
  })();
  const allCandidateIds = new Set(
    steps.flatMap((item) => item.candidates.map((candidate) => candidate.uuid)),
  );
  const unlinkedActions = trace.actions.filter(
    (item) => !item.candidate_uuid || !allCandidateIds.has(item.candidate_uuid),
  );
  const activeCandidate = previewCandidate ?? drawerCandidateUuid;
  const boardRef = useRef<HTMLDivElement>(null);
  const candidateNodes = useRef(new Map<string, HTMLButtonElement>());
  const effectNodes = useRef(new Map<string, HTMLElement>());
  const [branchLayout, setBranchLayout] = useState<BranchLayout>({
    width: 0,
    height: 0,
    branches: [],
  });

  useLayoutEffect(() => {
    const board = boardRef.current;
    if (!board) return;
    const measure = () => {
      const boardBounds = board.getBoundingClientRect();
      const branches = branchPairs.flatMap(({ candidateUuid, target }) => {
        const source = candidateNodes.current
          .get(candidateUuid)
          ?.getBoundingClientRect();
        const destination = effectNodes.current
          .get(target)
          ?.getBoundingClientRect();
        if (!source || !destination) return [];
        const horizontal = destination.left > source.right + 8;
        const x1 =
          (horizontal ? source.right : source.left + source.width / 2) -
          boardBounds.left;
        const y1 =
          (horizontal ? source.top + source.height / 2 : source.bottom) -
          boardBounds.top;
        const x2 =
          (horizontal
            ? destination.left
            : destination.left + destination.width / 2) - boardBounds.left;
        const y2 =
          (horizontal
            ? destination.top + destination.height / 2
            : destination.top) - boardBounds.top;
        const path = horizontal
          ? `M ${x1} ${y1} C ${(x1 + x2) / 2} ${y1}, ${(x1 + x2) / 2} ${y2}, ${x2} ${y2}`
          : `M ${x1} ${y1} C ${x1} ${(y1 + y2) / 2}, ${x2} ${(y1 + y2) / 2}, ${x2} ${y2}`;
        return [{ candidateUuid, target, path }];
      });
      const width = boardBounds.width;
      const height = boardBounds.height;
      setBranchLayout((previous) =>
        previous.width === width &&
        previous.height === height &&
        previous.branches.map((branch) => branch.path).join("|") ===
          branches.map((branch) => branch.path).join("|")
          ? previous
          : { width, height, branches },
      );
    };
    const observer = new ResizeObserver(measure);
    observer.observe(board);
    candidateNodes.current.forEach((node) => observer.observe(node));
    effectNodes.current.forEach((node) => observer.observe(node));
    measure();
    return () => observer.disconnect();
  }, [branchPairs]);

  if (!trace.decision)
    return (
      <EmptyNote>Decision has not been recorded for this event.</EmptyNote>
    );

  return (
    <div className="space-y-4" data-dd-privacy="hidden">
      {steps.length > 1 && (
        <nav aria-label="Decision steps" className="flex flex-wrap gap-2">
          {steps.map((item) => (
            <Button
              key={item.name}
              size="sm"
              variant={item.name === step?.name ? "primary" : "outline"}
              aria-current={item.name === step?.name ? "step" : undefined}
              onClick={() => {
                setStepName(item.name);
                setPreviewCandidate(null);
                setDrawerCandidateUuid(null);
              }}
            >
              {words(item.name)}
            </Button>
          ))}
        </nav>
      )}
      <p className="text-mono-caps text-xs text-annotation">
        Event + context → applied policy → selected effects
      </p>
      <div
        ref={boardRef}
        className="relative grid gap-4 xl:grid-cols-[minmax(10rem,1fr)_minmax(20rem,2fr)_minmax(12rem,1fr)]"
      >
        {branchLayout.width > 0 && (
          <svg
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 z-0 h-full w-full overflow-visible"
            viewBox={`0 0 ${branchLayout.width} ${branchLayout.height}`}
            preserveAspectRatio="none"
          >
            {branchLayout.branches.map((branch) => (
              <path
                key={`${branch.candidateUuid}:${branch.target}`}
                d={branch.path}
                fill="none"
                strokeWidth={
                  activeCandidate === branch.candidateUuid ? 2.5 : 1.5
                }
                className={
                  activeCandidate === branch.candidateUuid
                    ? "stroke-accent"
                    : "stroke-rule-strong"
                }
              />
            ))}
          </svg>
        )}
        <div className="relative z-10 space-y-3">
          <div className="rounded-paper border border-rule bg-card p-3">
            <p className="text-mono-caps text-xs text-annotation">Event</p>
            <p className="mt-1 font-medium text-ink">
              {words(trace.summary.kind)}
            </p>
            {matchingCurrentSource?.text && (
              <p className="mt-2 whitespace-pre-wrap text-sm text-ink-body">
                {matchingCurrentSource.text}
              </p>
            )}
            {matchingCurrentSource?.text && (
              <p className="mt-2 text-xs text-annotation">
                Current linked source for this saved turn · may have changed
              </p>
            )}
          </div>
          <div className="rounded-paper border border-rule bg-card p-3">
            <p className="text-mono-caps text-xs text-annotation">
              Loaded context
            </p>
            <p className="mt-1 text-xs text-annotation">
              Recorded for this decision step
            </p>
            <details className="mt-2 text-xs text-ink-body">
              <summary className="cursor-pointer">View saved context</summary>
              <div className="mt-2">
                <Evidence label="Context" value={loadedContext} />
              </div>
            </details>
          </div>
        </div>
        <section
          className="relative z-10 min-w-0 rounded-sheet border border-rule-strong bg-sheet p-4"
          aria-label="Applied policy"
          onMouseLeave={schedulePreviewExit}
          onBlur={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget))
              schedulePreviewExit();
          }}
        >
          <div className="border-b border-rule pb-3">
            <p className="text-mono-caps text-xs text-annotation">
              Applied policy
            </p>
            <h3 className="mt-1 font-display text-lg text-ink">
              {trace.decision.policy_version || "Version unavailable"}
            </h3>
            <p className="mt-1 text-sm text-annotation">
              {step
                ? `${words(step.name)} · ${words(step.outcome)}`
                : `Recorded steps unavailable · ${words(trace.decision.outcome)}`}
              {step?.reason ? ` · ${words(step.reason)}` : ""}
            </p>
            {typeof step?.judgment?.latency_ms === "number" && (
              <p className="mt-1 font-mono text-xs text-annotation">
                Model observed: {duration(step.judgment.latency_ms)}
              </p>
            )}
            {step?.judgment && (
              <details className="mt-2 text-xs text-ink-body">
                <summary className="cursor-pointer">
                  View saved judgment
                </summary>
                <div className="mt-2">
                  <Evidence label="Judgment" value={step.judgment} />
                </div>
              </details>
            )}
          </div>
          {step?.candidates.length ? (
            <ol className="mt-3 space-y-2">
              {step.candidates.map((candidate) => {
                const isPreview = candidate.uuid === previewCandidate;
                const isDrawer = candidate.uuid === drawerCandidateUuid;
                const previewId = `trace-condition-preview-${candidate.uuid}`;
                const anchorRef: { current: HTMLButtonElement | null } = {
                  current: null,
                };
                return (
                  <li key={candidate.uuid}>
                    <button
                      ref={(node) => {
                        anchorRef.current = node;
                        if (node)
                          candidateNodes.current.set(candidate.uuid, node);
                        else candidateNodes.current.delete(candidate.uuid);
                      }}
                      type="button"
                      aria-haspopup="dialog"
                      aria-describedby={isPreview ? previewId : undefined}
                      onClick={() => {
                        cancelPreviewExit();
                        setPreviewCandidate(null);
                        setDrawerCandidateUuid(candidate.uuid);
                      }}
                      onMouseEnter={() => {
                        cancelPreviewExit();
                        setPreviewCandidate(candidate.uuid);
                      }}
                      onFocus={() => {
                        cancelPreviewExit();
                        setPreviewCandidate(candidate.uuid);
                      }}
                      className={`flex w-full min-w-0 flex-col items-start justify-between gap-2 rounded-paper border p-3 text-left focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent sm:flex-row sm:items-center ${isPreview || isDrawer ? "border-accent bg-tint-accent" : candidate.chosen ? "border-rule-strong bg-card" : "border-rule bg-card"}`}
                    >
                      <span className="min-w-0">
                        <span className="block break-words text-sm font-medium text-ink sm:truncate">
                          {words(candidate.action_type)}
                        </span>
                        <span className="mt-1 block text-xs text-annotation">
                          {candidate.failed_rule
                            ? `Failed condition: ${candidate.failed_rule}`
                            : candidate.question
                              ? `Question: ${candidate.question}`
                              : "Recorded evaluation"}
                        </span>
                      </span>
                      <span className="self-end font-mono text-xs text-ink-body sm:shrink-0 sm:self-auto">
                        {candidateVerdict(candidate)}
                      </span>
                    </button>
                    <Popover
                      id={previewId}
                      role="tooltip"
                      open={isPreview && !drawerCandidate}
                      onOpenChange={(open) => {
                        if (!open)
                          setPreviewCandidate((current) =>
                            current === candidate.uuid ? null : current,
                          );
                      }}
                      anchorRef={anchorRef}
                      placement="right-start"
                      onMouseEnter={cancelPreviewExit}
                      onMouseLeave={schedulePreviewExit}
                      className="max-h-[min(70vh,32rem)] w-[min(24rem,calc(100vw-1rem))] overflow-y-auto"
                      data-dd-privacy="hidden"
                    >
                      {isPreview && (
                        <PolicyConditionPreview candidate={candidate} />
                      )}
                    </Popover>
                  </li>
                );
              })}
            </ol>
          ) : (
            <EmptyNote className="h-auto">
              Candidate evaluations were not recorded for this step.
            </EmptyNote>
          )}
          {!trace.decision.context_available && (
            <p className="mt-3 text-xs text-annotation">
              Historical policy context is unavailable. Recorded evidence is
              shown as saved.
            </p>
          )}
        </section>
        <section
          className="relative z-10 min-w-0 space-y-3"
          aria-label="Selected effects"
        >
          <p className="text-mono-caps text-xs text-annotation">
            Selected effects
          </p>
          {actions.length > 0 && (
            <ul className="space-y-2">
              {actions.map((action) => (
                <ActionResult
                  key={action.uuid}
                  action={action}
                  highlighted={activeCandidate === action.candidate_uuid}
                  ref={(node) => {
                    const key = `action:${action.uuid}`;
                    if (node) effectNodes.current.set(key, node);
                    else effectNodes.current.delete(key);
                  }}
                />
              ))}
            </ul>
          )}
          {childSteps.map((child) => (
            <Button
              key={child.name}
              ref={(node) => {
                const key = `step:${child.name}`;
                if (node) effectNodes.current.set(key, node);
                else effectNodes.current.delete(key);
              }}
              variant="outline"
              size="sm"
              className={`relative w-full justify-start ${activeCandidate && step?.candidates.some((candidate) => candidate.uuid === activeCandidate && child.parent_candidate_index === candidate.candidate_index) ? "border-accent" : ""}`}
              onClick={() => setStepName(child.name)}
            >
              Continue to {words(child.name)}
            </Button>
          ))}
          {actions.length === 0 && childSteps.length === 0 && (
            <p className="rounded-paper border border-rule bg-card p-3 text-sm text-annotation">
              {(step?.outcome ?? trace.decision.outcome) === "no_op"
                ? "Decision: no action selected."
                : "No downstream effect recorded for this step."}
            </p>
          )}
        </section>
      </div>
      <Dialog
        open={drawerCandidate !== null}
        onClose={() => setDrawerCandidateUuid(null)}
        size="md"
        title={
          drawerCandidate
            ? `${words(drawerCandidate.action_type)} · evidence`
            : "Candidate evidence"
        }
        className="m-0 ml-auto h-dvh max-h-dvh w-[min(42rem,100vw)] max-w-none"
        data-dd-privacy="hidden"
        footer={
          <Button
            variant="outline"
            onClick={() => setDrawerCandidateUuid(null)}
          >
            Close
          </Button>
        }
      >
        {drawerCandidate && step && (
          <div>
            {drawerCandidate.evaluation_status === "held" &&
              (!drawerEvidence?.hasFallbackGate ||
                drawerEvidence.fallbackEligible === true) && (
                <p className="text-sm text-ink-body">
                  Conditions met.{" "}
                  {earlierSelected
                    ? `${words(earlierSelected.action_type)} was selected first in policy order.`
                    : "This candidate was not selected; the recorded reason is unavailable."}
                </p>
              )}
            <PolicyConditions
              key={drawerCandidate.uuid}
              candidate={drawerCandidate}
              step={step}
              loaded={loadedContext ?? null}
              source={trace.summary.source}
            />
          </div>
        )}
      </Dialog>
      {unlinkedActions.length > 0 && (
        <section className="border-t border-rule pt-3">
          <p className="text-mono-caps text-xs text-annotation">
            Actions without a recorded candidate link
          </p>
          <ul className="mt-2 grid gap-2 md:grid-cols-2">
            {unlinkedActions.map((action) => (
              <ActionResult
                key={action.uuid}
                action={action}
                highlighted={false}
              />
            ))}
          </ul>
        </section>
      )}
      <details className="border-t border-rule pt-3 text-xs text-ink-body">
        <summary className="cursor-pointer">Raw recorded decision</summary>
        <div className="mt-2">
          <Evidence
            label="Historical decision context"
            value={trace.decision.raw_context}
          />
        </div>
      </details>
    </div>
  );
};
